import { useEffect, useState } from 'react'
import { assetUrl } from '../api'
import type { AdminSport, AdminSportsbook, SportDivision } from './api'
import { updateAdminSportsbook } from './api'
import { SportsbookCarriedPanel } from './CarriedByPanel'
import SportsbookBrandPanel from './SportsbookBrandPanel'
import { isEffectivelyBlack, pickReadableText } from './contrast'
import StateGrid from './StateGrid'
import StringList from './StringList'

interface Props {
  sportsbook: AdminSportsbook
  sports: AdminSport[]
  divisions: SportDivision[]
  canEdit: boolean
  onSaved: () => void
}

type Mode = 'view' | 'edit'


export default function SportsbookEditor({ sportsbook, sports, divisions, canEdit, onSaved }: Props) {
  const [mode, setMode] = useState<Mode>('view')
  const [name, setName] = useState(sportsbook.name)
  const [active, setActive] = useState<string[]>(
    sportsbook.sportsbook_states.map(s => s.state_code).sort(),
  )
  const [allowed, setAllowed] = useState<string[]>(
    sportsbook.sportsbook_international.filter(r => r.kind === 'allowed').map(r => r.region).sort(),
  )
  const [blocked, setBlocked] = useState<string[]>(
    sportsbook.sportsbook_international.filter(r => r.kind === 'blocked').map(r => r.region).sort(),
  )
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  useEffect(() => {
    setMode('view')
    setName(sportsbook.name)
    setActive(sportsbook.sportsbook_states.map(s => s.state_code).sort())
    setAllowed(sportsbook.sportsbook_international.filter(r => r.kind === 'allowed').map(r => r.region).sort())
    setBlocked(sportsbook.sportsbook_international.filter(r => r.kind === 'blocked').map(r => r.region).sort())
    setMsg(null)
  }, [sportsbook.id])

  function cancel() {
    setName(sportsbook.name)
    setActive(sportsbook.sportsbook_states.map(s => s.state_code).sort())
    setAllowed(sportsbook.sportsbook_international.filter(r => r.kind === 'allowed').map(r => r.region).sort())
    setBlocked(sportsbook.sportsbook_international.filter(r => r.kind === 'blocked').map(r => r.region).sort())
    setMode('view')
    setMsg(null)
  }

  async function save() {
    setBusy(true)
    setMsg(null)
    try {
      await updateAdminSportsbook(sportsbook.id, {
        name: name.trim(),
        status: null,
        active_states: active,
        international: allowed,
        international_blocked: blocked,
      })
      setMsg({ kind: 'ok', text: `Saved ${name.trim()}.` })
      setMode('view')
      onSaved()
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  const readOnly = mode === 'view'
  // Blocked (denylist) regions are available on every book in edit mode; in
  // view mode we only render the section when it actually holds data so books
  // without a denylist stay uncluttered.
  const showsBlocked = !readOnly || blocked.length > 0

  // Per-book accent: drive the active-highlight color in this editor from
  // the operator's brand color, with text auto-flipped to whichever of
  // black/white wins the WCAG contrast check. Falls back to the default
  // ink/paper theme when brand_color is missing, unparseable, or
  // effectively black — the default theme already flips with dark mode
  // so a black-branded book reads correctly on either background.
  const rawBrand = sportsbook.brand_color ?? null
  const brandBg = rawBrand && !isEffectivelyBlack(rawBrand) ? rawBrand : null
  const brandFg = brandBg ? pickReadableText(brandBg, sportsbook.name) : null
  const brandStyle = brandBg && brandFg
    ? ({ '--brand-active-bg': brandBg, '--brand-active-fg': brandFg } as React.CSSProperties)
    : undefined

  return (
    <section className={`ad-editor${readOnly ? ' is-read-only' : ''}${brandStyle ? ' is-branded' : ''}`} style={brandStyle}>
      <header className="ad-editor-head">
        <div className="ad-editor-head-stack">
          {(sportsbook.brand_color || sportsbook.logo_url) && (
            <div
              className={`ad-editor-logo${sportsbook.logo_fill ? ' is-fill' : ''}`}
              style={{ background: sportsbook.brand_color ?? 'transparent' }}
              data-book={sportsbook.name.toLowerCase().replace(/\s+/g, '-')}
            >
              {sportsbook.logo_url
                ? <img src={assetUrl(sportsbook.logo_url)} alt={sportsbook.name} loading="lazy" />
                : <span className="ad-editor-logo-text">{sportsbook.name.slice(0, 2).toUpperCase()}</span>}
            </div>
          )}
          <h2 className="b-h2">{sportsbook.name}</h2>
        </div>
        <div className="ad-editor-actions">
          {mode === 'view' && canEdit && (
            <button
              type="button"
              onClick={() => { setMode('edit'); setMsg(null) }}
              className="b-btn b-btn-primary"
            >
              EDIT →
            </button>
          )}
          {mode === 'edit' && (
            <>
              <button
                type="button"
                onClick={cancel}
                disabled={busy}
                className="b-btn-ghost"
              >
                CANCEL
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="b-btn b-btn-primary"
              >
                {busy ? 'SAVING…' : 'SAVE →'}
              </button>
            </>
          )}
        </div>
      </header>

      {msg && (
        <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>
          {msg.kind === 'err' ? '⚠' : '✓'} {msg.text}
        </p>
      )}

      <div className="ad-editor-row">
        <label className="ad-field">
          <span className="b-mono-dim b-tiny">NAME</span>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={readOnly}
            className="ad-input"
          />
        </label>
      </div>

      {/* Brand identity has its own EDIT/SAVE (separate RPC, includes the
          logo upload) — trading+ can edit it here without the /affiliate tab. */}
      <SportsbookBrandPanel book={sportsbook} canEdit={canEdit} onSaved={onSaved} />

      <SportsbookCarriedPanel
        sportsbookId={sportsbook.id}
        sports={sports}
        divisions={divisions}
        canEdit={canEdit}
      />

      <div className="ad-editor-section">
        <h3 className="b-mono">ACTIVE US STATES</h3>
        <StateGrid selected={active} onChange={setActive} readOnly={readOnly} />
      </div>

      <div className="ad-editor-section">
        <h3 className="b-mono">INTERNATIONAL</h3>
        <StringList
          label="ACTIVE REGIONS"
          values={allowed}
          onChange={setAllowed}
          placeholder="e.g. Ontario, Argentina (comma-separated ok)"
          readOnly={readOnly}
        />
        {showsBlocked && (
          <StringList
            label="BLOCKED REGIONS"
            values={blocked}
            onChange={setBlocked}
            placeholder="e.g. Germany, Italy, Spain (comma-separated ok)"
            readOnly={readOnly}
          />
        )}
      </div>
    </section>
  )
}
